/**
 * Date utility functions for the date tracking feature
 */

/**
 * Safely parse a date string that may be timezone-naive or timezone-aware.
 * Handles both old format (2024-10-17T12:30:45.123456) and new format (2024-10-17T12:30:45.123456+00:00)
 * 
 * @param dateString - ISO datetime string from backend
 * @returns Date object
 */
export const parseDateSafe = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  // JavaScript's Date constructor handles both naive and timezone-aware ISO strings correctly
  // We just need to make sure we don't add 'Z' if timezone is already present
  let normalizedString = dateString;
  
  // If no timezone info (no '+', '-', or 'Z'), assume UTC and add 'Z'
  if (!dateString.includes('+') && !dateString.includes('-', 10) && !dateString.includes('Z')) {
    normalizedString = dateString + 'Z';
  }
  
  return new Date(normalizedString);
};

/**
 * Format a date as a relative time string (e.g., "2 days ago", "3 weeks ago")
 */
export const formatRelativeDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const now = new Date();
    const target = parseDateSafe(dateString);
    
    // Reset time to start of day for accurate day comparison
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    
    const diffInDays = Math.floor((nowStart.getTime() - targetStart.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'today';
    if (diffInDays === 1) return 'yesterday';
    if (diffInDays > 0 && diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays > 0 && diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      const days = diffInDays % 7;
      return `${weeks} week${weeks > 1 ? 's' : ''}${days > 0 ? ` and ${days} day${days > 1 ? 's' : ''}` : ''} ago`;
    }
    if (diffInDays > 0 && diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      const days = diffInDays % 30;
      return `${months} month${months > 1 ? 's' : ''}${days > 0 ? ` and ${days} day${days > 1 ? 's' : ''}` : ''} ago`;
    }
    if (diffInDays > 0) {
      return `${Math.floor(diffInDays / 365)} year${Math.floor(diffInDays / 365) > 1 ? 's' : ''} ago`;
    }
    
    // If date is in the future, show as "today"
    return 'today';
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

/**
 * Format a date as an absolute date string (e.g., "Jan 15, 2024")
 */
export const formatAbsoluteDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = parseDateSafe(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting absolute date:', error);
    return '';
  }
};

/**
 * Calculate duration between two dates in days
 */
export const calculateDuration = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;
  
  try {
    const start = parseDateSafe(startDate);
    const end = parseDateSafe(endDate);
    const diffInDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffInDays);
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 0;
  }
};

/**
 * Format journey date for display (e.g., "4 weeks and 2 days ago (Jan 15, 2024)")
 */
export const formatJourneyDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const relative = formatRelativeDate(dateString);
  const absolute = formatAbsoluteDate(dateString);
  
  return `${relative} (${absolute})`;
};

/**
 * Validate that a date is not in the future
 */
export const validateDateNotInFuture = (dateString: string): boolean => {
  if (!dateString) return true;
  
  try {
    const date = parseDateSafe(dateString);
    const now = new Date();
    return date <= now;
  } catch (error) {
    return false;
  }
};

/**
 * Validate that start date is before end date
 */
export const validateStartBeforeEnd = (startDate: string, endDate: string): boolean => {
  if (!startDate || !endDate) return true;
  
  try {
    const start = parseDateSafe(startDate);
    const end = parseDateSafe(endDate);
    return start <= end;
  } catch (error) {
    return false;
  }
}; 